import React, { useEffect, useState, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Badge, Button, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import { CONCERN_CATEGORIES, CONCERN_STATUS, IV_ACTIONS, formatThaiDate } from "../utils/careUtils";

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function MyCare() {
  const { setLoading } = useLoading();
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [, setSkin] = useState(currentSkin);

  const [concerns, setConcerns] = useState([]);
  const [followups, setFollowups] = useState([]);

  const loadData = useCallback(() => {
    setLoading(true);
    axiosInstance
      .get(`${process.env.REACT_APP_API}/care/myCare`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((res) => {
        setConcerns(res.data.concerns || []);
        setFollowups(res.data.followups || []);
      })
      .catch((err) => {
        Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || err.message });
      })
      .finally(() => setLoading(false));
  }, [setLoading]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleFollowup = (iv, done) => {
    axiosInstance
      .put(`${process.env.REACT_APP_API}/care/followupDone/${iv.iv_id}`, { done }, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then(() => loadData())
      .catch((err) => {
        Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || err.message });
      });
  };

  const overdue = followups.filter((f) => f.iv_followup_date < todayIso());

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/teaching/saveGrade">การสอน</Link></li>
              <li className="breadcrumb-item active">ดูแลช่วยเหลือนักเรียน</li>
            </ol>
            <h4 className="main-title mb-0">ดูแลช่วยเหลือนักเรียนของฉัน</h4>
            <p className="text-muted mb-0 fs-sm">เรื่องที่ฉันแจ้งดูแลซึ่งยังไม่ยุติ และนัดติดตามผลที่ยังค้าง</p>
          </div>
        </div>

        <Row className="g-3 mb-4">
          <Col md={6}>
            <Card className="card-one h-100">
              <Card.Header className="d-flex align-items-center gap-2">
                <Card.Title as="h6" className="mb-0">นัดติดตามผลที่ค้าง</Card.Title>
                <Badge bg={overdue.length > 0 ? "danger" : "secondary"}>{followups.length}</Badge>
                {overdue.length > 0 && <small className="text-danger">เลยกำหนด {overdue.length} รายการ</small>}
              </Card.Header>
              <Card.Body>
                {followups.length === 0 ? (
                  <div className="text-center text-muted py-3">ไม่มีนัดติดตามผลค้าง</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {followups.map((f) => (
                      <div key={f.iv_id}
                        className={`border rounded p-2 d-flex align-items-start justify-content-between gap-2 ${f.iv_followup_date < todayIso() ? 'border-danger' : ''}`}>
                        <div className="fs-sm">
                          <div className="fw-semibold">{f.st_name} <span className="text-muted">({f.st_idnumber})</span></div>
                          <div className="text-muted">
                            {f.sub_subid ? `${f.sub_subid} ${f.sub_subname} · ` : ""}
                            {IV_ACTIONS[f.iv_action] || f.iv_action}
                            {f.iv_detail && ` — ${f.iv_detail}`}
                          </div>
                          <div className={f.iv_followup_date < todayIso() ? "text-danger" : "text-muted"}>
                            นัดติดตาม {formatThaiDate(f.iv_followup_date)}
                          </div>
                        </div>
                        <Form.Check
                          type="checkbox"
                          id={`mycare-fu-${f.iv_id}`}
                          title="ติดตามแล้ว"
                          checked={false}
                          onChange={() => toggleFollowup(f, true)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="card-one h-100">
              <Card.Header className="d-flex align-items-center gap-2">
                <Card.Title as="h6" className="mb-0">เรื่องที่ฉันแจ้งดูแล (ยังไม่ยุติ)</Card.Title>
                <Badge bg="warning">{concerns.length}</Badge>
              </Card.Header>
              <Card.Body>
                {concerns.length === 0 ? (
                  <div className="text-center text-muted py-3">ไม่มีเรื่องที่ยังไม่ยุติ</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {concerns.map((c) => {
                      const status = CONCERN_STATUS[c.concern_status] || {};
                      return (
                        <div key={c.concern_id} className="border rounded p-2 d-flex align-items-start justify-content-between gap-2">
                          <div className="fs-sm">
                            <div className="fw-semibold">{c.st_name} <span className="text-muted">({c.st_idnumber})</span></div>
                            <div>
                              <Badge bg="secondary" className="me-1">{CONCERN_CATEGORIES[c.concern_category] || c.concern_category}</Badge>
                              <Badge bg={status.variant || "secondary"}>{status.label || c.concern_status}</Badge>
                              {c.sub_subid && <span className="text-muted ms-1">{c.sub_subid} {c.sub_subname}</span>}
                            </div>
                            <div className="text-muted text-truncate" style={{ maxWidth: 320 }}>{c.concern_detail}</div>
                          </div>
                          {c.teach_id && (
                            <Button as={Link} to={`/teaching/subject-care/${c.teach_id}`} variant="white" size="sm"
                              title="ไปหน้าการแจ้งดูแลของวิชา">
                              <i className="ri-arrow-right-line" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </React.Fragment>
  );
}
